import axiosInstance from '@/api/axiosInstance';

export interface SecurityConfig {
  id?: number;
  prevent_multiple_logins: boolean;
  max_file_size_mb: number;
  account_lockout_threshold: number;
  account_lockout_duration_minutes: number;
  password_min_length: number;
  password_require_letters: boolean;
  password_require_numbers: boolean;
  password_require_special: boolean;
  password_rotation_limit: number;
  allow_frame_embedding: boolean;
  session_idle_timeout_minutes: number;
  allowed_file_extensions: string;
  /** Colleagues see each other's e-mail addresses in the people directory (UAT DEF-016/152). */
  directory_show_email?: boolean;
  /** Visitors who are not signed in can browse / search the catalogue and open course details (default on). */
  public_catalog_enabled?: boolean;
  /** GDPR retention: purge (anonymise) accounts deleted more than N days ago, daily. */
  gdpr_auto_purge_enabled?: boolean;
  gdpr_purge_after_days?: number;
  // HTTP security headers (API responses and, through the web server, the app's pages).
  hsts_enabled?: boolean;
  hsts_max_age_seconds?: number;
  hsts_include_subdomains?: boolean;
  hsts_preload?: boolean;
  content_type_nosniff?: boolean;
  referrer_policy?: string;
  permissions_policy?: string;
  cross_origin_opener_policy?: string;
  content_security_policy?: string;
  csp_report_only?: boolean;
  created_at?: string;
  updated_at?: string;
}

const BASE = '/v1/security-config';

export const SecurityApi = {
  /** What any signed-in user may upload (the full config is admin-only — UAT DEF-FM-001). */
  async getUploadPolicy(): Promise<{ allowed_file_extensions: string; max_file_size_mb?: number; blocked_file_extensions?: string[] }> {
    const { data } = await axiosInstance.get<{ allowed_file_extensions: string; max_file_size_mb?: number; blocked_file_extensions?: string[] }>(`${BASE}/upload-policy`);
    return data;
  },
  /** Whether this admin edits the site-wide part (HTTPS and the security headers). */
  async getScope(): Promise<{ can_edit_site_headers: boolean }> {
    const { data } = await axiosInstance.get(`${BASE}/scope`);
    return data;
  },
  /** The Security page's suggestions: recommended file types, the always-blocked ones, a starting CSP. */
  async getDefaults(): Promise<{ recommended_file_extensions: string[]; blocked_file_extensions: string[]; recommended_content_security_policy: string }> {
    const { data } = await axiosInstance.get(`${BASE}/defaults`);
    return data;
  },
  async get(): Promise<SecurityConfig> {
    const res = await axiosInstance.get<SecurityConfig>(BASE + '/');
    return res.data;
  },
  async save(data: SecurityConfig): Promise<SecurityConfig> {
    const res = await axiosInstance.put<SecurityConfig>(BASE + '/', data);
    return res.data;
  },
};

// ── GDPR privacy: purge deleted accounts (manual / policy) and the purge log ─────────────────
export interface PurgeLogEntry {
  id: number;
  user_id: number | null;
  purged_at: string;
  purged_by_user_id: number | null;
  policy: 'manual' | 'automatic' | string;
  deleted_at: string | null;
  details: { removed?: Record<string, number>; anonymised?: string[] } | null;
}

const PRIVACY = '/v1/privacy';

export const PrivacyApi = {
  /** Anonymise one deleted account; its learning history stays under its id. */
  async purgeUser(userId: number): Promise<{ user_id: number; removed: Record<string, number> }> {
    const res = await axiosInstance.post(`${PRIVACY}/users/${userId}/purge`);
    return res.data;
  },
  async previewExpired(days?: number): Promise<{ enabled: boolean; purge_after_days: number; user_ids: number[]; count: number }> {
    const res = await axiosInstance.get(`${PRIVACY}/purge-expired/preview`, { params: days ? { days } : {} });
    return res.data;
  },
  async purgeExpired(days?: number): Promise<{ purged_user_ids: number[]; count: number }> {
    const res = await axiosInstance.post(`${PRIVACY}/purge-expired`, null, { params: days ? { days } : {} });
    return res.data;
  },
  async logs(limit = 20, offset = 0): Promise<{ items: PurgeLogEntry[]; total: number }> {
    const res = await axiosInstance.get(`${PRIVACY}/purge-logs`, { params: { limit, offset } });
    return res.data;
  },
};
